import { useEffect, useMemo, useState, useCallback } from "react";
import { Trans, useTranslation } from "react-i18next";
import { SortingState } from "@tanstack/react-table";
import { Printer, Search } from "lucide-react";
import usePagination from "@/hooks/useSyncedPagination";
import { ActionDataBo } from "@/types/tableAction";
import {
  useGetAllExpeditionBo,
  useGetExpeditionCountsValidatedSummaryBo,
} from "@/api/expedition.api";
import { UnifiedInput } from "@/components/ui/customs/unified-input";
import { BlockedExpeditionsTable } from "@/components/backOffice/validatedExpeditions/blockedExpeditionsTable";
import { UnblockedExpeditionsTable } from "@/components/backOffice/validatedExpeditions/unblockedExpeditionsTable";
import { ChartDayPicker } from "@/components/backOffice/validatedExpeditions/chartDayPicker";
import {
  BlockedExpeditionsStatusBox,
  type BlockedView,
} from "@/components/backOffice/validatedExpeditions/blockedExpeditionsStatusBox";
import { ValidatedExpeditionsFilters } from "@/components/backOffice/validatedExpeditions/validatedExpeditionsFilters";
import {
  useValidatedExpeditionsFilters,
  deliveryStatusGroupToParams,
} from "@/components/backOffice/validatedExpeditions/useValidatedExpeditionsFilters.reducer";
import { COUNTRY_REGIONS } from "@/constants/countries";
import { DeliveryStatusEnum } from "@/constants/enums/deliveryStatus.enum";
import { ValidatedExpeditionsModals } from "@/components/backOffice/validatedExpeditions/validatedExpeditionsModals";
import { ExpeditionTypeEnum } from "@/constants/enums/expeditionType.enum";
import { PrintShippingLabelsModal } from "@/components/backOffice/validatedExpeditions/modal/printShippingLabels.modal";
import { Button } from "@/components/ui/button";

const BLOCKED_GROUP_PARAMS = deliveryStatusGroupToParams("blocked");
const UNBLOCKED_PARAMS = { deliveryStatus: [DeliveryStatusEnum.ONGOING], isUnblocked: true };

export const BlockedExpeditionsV2Page = () => {
  const { t } = useTranslation("boExpeditions");
  const [actionData, setActionData] = useState<ActionDataBo | null>(null);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([{ id: "blockedAt", desc: true }]);
  const [pagination, setPagination] = usePagination("blockedExpeditionsV2");
  const { filters, debouncedSearch, actions } = useValidatedExpeditionsFilters();

  const [chartMonth, setChartMonth] = useState(() => {
    const from = filters.finalizedAt?.[0] ?? new Date();
    return new Date(from.getFullYear(), from.getMonth(), 1);
  });

  const [blockedView, setBlockedView] = useState<BlockedView>("blocked");
  const isUnblockedView = blockedView === "unblocked";

  const handleBlockedViewChange = useCallback((view: BlockedView) => {
    setBlockedView(view);
    setSorting([{ id: view === "unblocked" ? "unblockedAt" : "blockedAt", desc: true }]);
  }, []);

  useEffect(() => {
    if (filters.finalizedDateFilterType === "MONTH") {
      actions.setFinalizedMonth(true, chartMonth);
    }
    // Only re-sync when the navigated month changes, not on every filter change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartMonth]);

  const handleToggleMonthFilter = useCallback(() => {
    actions.setFinalizedMonth(filters.finalizedDateFilterType !== "MONTH", chartMonth);
  }, [actions, filters.finalizedDateFilterType, chartMonth]);

  const pickupDateFilter = useMemo(() => {
    if (!filters.pickupDate) return {};
    return {
      pickupDateAfter: filters.pickupDate[0].toISOString(),
      pickupDateBefore: filters.pickupDate[1].toISOString(),
    };
  }, [filters.pickupDate]);

  const finalizedAtFilter = useMemo(() => {
    if (!filters.finalizedAt) return {};
    return {
      finalizedAfter: filters.finalizedAt[0].toISOString(),
      finalizedBefore: filters.finalizedAt[1].toISOString(),
    };
  }, [filters.finalizedAt]);

  const expeditionQueryFilter = useMemo(
    () => ({
      processStatus: ["VALIDATED"] as string[],
      ...finalizedAtFilter,
      ...(isUnblockedView ? UNBLOCKED_PARAMS : BLOCKED_GROUP_PARAMS),
      ...(filters.isExpress ? { isExpress: true } : {}),
      ...(filters.isRemote ? { isRemote: true } : {}),
      ...(filters.isCustomQuotation ? { quotationType: ExpeditionTypeEnum.CUSTOM_QUOTATION } : {}),
      ...(filters.isFinalizedBySelf ? { finalizedBySelf: true } : {}),
      ...(filters.finalizedById ? { finalizedById: filters.finalizedById } : {}),
      ...(filters.country
        ? { country: filters.country }
        : filters.region
          ? { countries: COUNTRY_REGIONS[filters.region] }
          : {}),
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(filters.searchExact ? { searchExact: true } : {}),
      ...(filters.customId ? { customId: filters.customId } : {}),
      ...(filters.packagingType ? { packagingType: filters.packagingType } : {}),
      ...(filters.manualTracking
        ? { manualTracking: filters.manualTracking === "manualTracking" }
        : {}),
      ...(!isUnblockedView && filters.blockedManual !== null
        ? { blockedManual: filters.blockedManual }
        : {}),
      ...(!isUnblockedView && filters.blockedReason
        ? { blockedReason: filters.blockedReason }
        : {}),
      ...pickupDateFilter,
    }),
    [
      isUnblockedView,
      finalizedAtFilter,
      pickupDateFilter,
      filters.isExpress,
      filters.isRemote,
      filters.isCustomQuotation,
      filters.isFinalizedBySelf,
      filters.finalizedById,
      filters.country,
      filters.region,
      debouncedSearch,
      filters.searchExact,
      filters.customId,
      filters.packagingType,
      filters.manualTracking,
      filters.blockedManual,
      filters.blockedReason,
    ],
  );

  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [expeditionQueryFilter, setPagination]);

  // Static counts for the status box tabs — independent of active filters.
  const { data: staticCountsSummary } = useGetExpeditionCountsValidatedSummaryBo({
    processStatus: ["VALIDATED"],
    ...BLOCKED_GROUP_PARAMS,
  });

  const blockedViewCounts = useMemo(() => {
    const quickFilters = staticCountsSummary?.quickFilters;
    if (!quickFilters) return undefined;
    return {
      blocked: quickFilters.blocked,
      unblocked: quickFilters.unblocked,
    };
  }, [staticCountsSummary]);

  // Filtered counts for the other quick filters
  const { data: countsSummary } = useGetExpeditionCountsValidatedSummaryBo(expeditionQueryFilter);

  const { data: expeditions, isLoading: isExpeditionsLoading } = useGetAllExpeditionBo({
    ...expeditionQueryFilter,
    take: pagination.pageSize,
    skip: pagination.pageIndex * pagination.pageSize,
    sortBy: sorting[0]?.id,
    sortOrder: sorting[0]?.desc ? "DESC" : "ASC",
  });

  const expeditionsData = useMemo(() => expeditions?.data ?? [], [expeditions]);

  return (
    <div className="bg-border/20 flex h-fit min-h-dvh w-full flex-1 flex-col gap-2 p-8">
      <div className="flex w-full flex-col justify-between gap-2 md:flex-row">
        <div className="flex w-fit flex-col gap-2">
          <h1 className="font-jakarta-sans text-3xl font-semibold">
            {isUnblockedView ? (
              <Trans
                t={t}
                i18nKey="blockedPage.unblockedTitle"
                components={{ highlight: <span className="text-green-600" /> }}
              />
            ) : (
              <Trans
                t={t}
                i18nKey="blockedPage.blockedTitle"
                components={{ highlight: <span className="text-orange-500" /> }}
              />
            )}
          </h1>
          <p>
            {isUnblockedView
              ? t("blockedPage.unblockedSubtitle")
              : t("blockedPage.blockedSubtitle")}
          </p>
        </div>
        <div className="flex flex-col items-end gap-4 py-2">
          <div className="flex items-center gap-4">
            <div className="flex flex-row items-center gap-2">
              <UnifiedInput
                inputType="text"
                leftElement={<Search />}
                value={filters.search}
                onChange={(e) => actions.setSearch(String(e || ""))}
                className="placeholder:text-[#94A3B8]"
                placeholder={t("blockedPage.searchPlaceholder")}
              />
              <Button variant="outline" onClick={() => setPrintModalOpen(true)} className="h-10">
                <Printer className="h-6 w-6" />
              </Button>
            </div>
          </div>
        </div>
      </div>
      {!isUnblockedView && (
        <ChartDayPicker
          currentMonth={chartMonth}
          onMonthChange={setChartMonth}
          selectedDay={
            filters.finalizedDateFilterType === "DAY" ? filters.finalizedAt?.[0] ?? null : null
          }
          onSelectedDayChange={actions.setFinalizedDay}
          isActive={filters.finalizedDateFilterType === "MONTH"}
          onToggle={handleToggleMonthFilter}
          extraFilter={BLOCKED_GROUP_PARAMS}
          mode="blocked"
        />
      )}
      <BlockedExpeditionsStatusBox
        view={blockedView}
        onViewChange={handleBlockedViewChange}
        counts={blockedViewCounts}
      />
      <ValidatedExpeditionsFilters
        group={isUnblockedView ? "ongoing" : "blocked"}
        filters={filters}
        actions={actions}
        counts={countsSummary?.quickFilters}
        showShippingLate={false}
      />
      {isUnblockedView ? (
        <UnblockedExpeditionsTable
          data={expeditionsData}
          rowCount={expeditions?.count || 0}
          pagination={pagination}
          setPagination={setPagination}
          sorting={sorting}
          setSorting={setSorting}
          setActionData={setActionData}
          isLoading={isExpeditionsLoading}
        />
      ) : (
        <BlockedExpeditionsTable
          data={expeditionsData}
          rowCount={expeditions?.count || 0}
          pagination={pagination}
          setPagination={setPagination}
          sorting={sorting}
          setSorting={setSorting}
          setActionData={setActionData}
          isLoading={isExpeditionsLoading}
        />
      )}
      <ValidatedExpeditionsModals
        actionData={actionData}
        onClose={() => setActionData(null)}
        setActionData={setActionData}
        isDeleted={false}
      />
      {printModalOpen && (
        <PrintShippingLabelsModal open={true} onClose={() => setPrintModalOpen(false)} />
      )}
    </div>
  );
};
